import React from 'react';
import PropTypes from 'prop-types';
import LazyLoad from 'react-lazyload';
import { Config } from '@deporvillage/utils-library-component';

import cloudinary from './cloudinary.js';
import cloudflare from './cloudflare.js';

import styles from './Picture.module.scss';

const CdnProviders = {
    Cloudinary: 'cloudinary',
    Cloudflare: 'cloudflare',
};

const defaultSize = 900;
const isRelativePath = (p) => /^(?!(https?:)?\/\/)/i.test(p);

const getQuerySizes = (s) => Object.keys(s)
    .sort((a, b) => a - b)
    .map((k) => {
        let [width, height] = s[k].split('x');
        return { query: k, width, height };
    });

const renderSources = (sources) => (
    sources.map(({ query, dpi1, dpi2 }) => (
        <source
            key={ query }
            media={ `(max-width: ${ query }px)` }
            srcSet={ `${ dpi1 } 1x, ${ dpi2 } 2x` }
        />
    ))
);

const cdn = (params, { name, hosts }) => {
    switch (name) {
        case CdnProviders.Cloudinary:
            return cloudinary({ ...params, host: hosts[Math.floor(Math.random() * hosts.length)] });
        case CdnProviders.Cloudflare:
            return cloudflare({ ...params, host: hosts[Math.floor(Math.random() * hosts.length)] });
        default:
            return null;
    }
};

const Picture = ({ name, className, src, width, height, original, sizes,
                     lazy, crop,transparent, quality, resize, onMouseMove, priority }) => {

    const imgWidth = typeof width === 'string' ? defaultSize : width;
    const imgHeight = typeof height === 'string' ? defaultSize : height;
    const imgParams = { src, width: imgWidth, height: imgHeight, transparent, quality, crop, original };

    const { cdn: cdnConfig } = React.useContext(Config);

    const [dpi1, setDpi1] = React.useState(null);
    const [dpi2, setDpi2] = React.useState(null);
    const [dpiResponsive, setDpiResponsive] = React.useState(null);
    const [loaded, setLoaded] = React.useState(false);
    const [prevSrc, setPrevSrc] = React.useState(src);

    /* istanbul ignore next */
    const handleOnLoad = () => setLoaded(true);

    const updateSrc = () => {

        setDpi1(isRelativePath(src) ? cdn({ ...imgParams, dpr: 1 }, cdnConfig) : src);
        setDpi2(isRelativePath(src) ? cdn({ ...imgParams, dpr: 2 }, cdnConfig) : src);

        if (sizes) setDpiResponsive(
            getQuerySizes(sizes).map(({ query, width, height }) => {
                let dpi1 = cdn({ ...imgParams, width, height, dpr: 1 }, cdnConfig);
                let dpi2 = cdn({ ...imgParams, width, height, dpr: 2 }, cdnConfig);
                return { query, dpi1, dpi2 };
            })
        );
    };

    const renderImg = () => (
        <picture>
            { dpiResponsive ? renderSources(dpiResponsive) : null }
            <img
                onLoad={ handleOnLoad }
                src={ dpi1 }
                srcSet={ `${ dpi2 } 2x` }
                alt={ name }
                onMouseMove={ onMouseMove }
                height={ imgHeight }
                width={ imgWidth }
                fetchpriority= { priority || 'low' }
            />
        </picture>
    );

    React.useEffect(() => {
        if (prevSrc === src) return;
        setPrevSrc(src);
        updateSrc();
    }, [src]);

    if (!dpi1 || !dpi2) updateSrc();

    return (
        <div className={
            `${ styles['picture-component'] }${ className ? ` ${ className }` : `` } ${ lazy && !loaded ? styles['picture-lazy'] : '' }`
        }>
            { lazy ?
                <LazyLoad once height={ imgHeight } resize={ resize }>
                    { renderImg() }
                </LazyLoad>
                :
                renderImg()
            }
        </div>
    );
};

Picture.propTypes = {
    name: PropTypes.string,
    className: PropTypes.string,
    src: PropTypes.string,
    sizes: PropTypes.object,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    lazy: PropTypes.bool,
    transparent: PropTypes.bool,
    resize: PropTypes.bool,
    crop: PropTypes.bool,
    quality: PropTypes.number,
    original: PropTypes.bool,
    onMouseMove: PropTypes.func,
    priority: PropTypes.string,
};

export default Picture;
export { CdnProviders, cdn };
